/**
 * @file EmptyState.tsx
 * @description Пустое состояние списка (phase19.23 этап A).
 *
 * Три повода, три разных текста (чек-лист A6): данных ещё нет, ничего не
 * нашлось по фильтру, нет прав. Компактный блок `py-8` с иконкой 32px, одной
 * фразой и действием — раньше каждая страница рисовала свой вариант «py-10 и
 * абзац текста».
 *
 * ```tsx
 * <EmptyState
 *   icon={ClipboardList}
 *   variant="empty"
 *   title="Заданий на сборку нет"
 *   description="Появляются, когда заказ переходит на стадию сборки."
 *   action={<Button variant="outline">Открыть доску заказов</Button>}
 * />
 * ```
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Повод показать пустое состояние — от него зависят тон и рамка. */
export type EmptyStateVariant = "empty" | "filtered" | "forbidden";

interface Props {
  icon: LucideIcon;
  /** Одна фраза: что тут должно быть. */
  title: string;
  /** Пояснение «откуда это берётся» — по желанию. */
  description?: ReactNode;
  /** Кнопка/ссылка «что сделать дальше». */
  action?: ReactNode;
  variant?: EmptyStateVariant;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = "empty",
  className,
}: Props) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-8 text-center",
        variant === "forbidden" ? "border-amber-500/40" : "border-on-surface/15",
        className,
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          "size-8",
          variant === "forbidden" ? "text-amber-600" : "text-on-surface-variant",
        )}
      />
      <p className="text-sm font-medium text-on-surface">{title}</p>
      {description ? (
        <p className="max-w-[46ch] text-sm text-on-surface-variant">{description}</p>
      ) : null}
      {action ? (
        <div className="mt-1 flex flex-wrap justify-center gap-2">{action}</div>
      ) : null}
    </div>
  );
}
